import { useCallback, useEffect, useState } from 'react';

import {
  FalloIngreso,
  ingresarConPasskey,
  type MotivoFallo,
  type OpcionesDeIngreso,
  opcionesVigentes,
  pedirOpciones,
  reportarFallo,
} from '../passkeys/api/passkeyLoginService';

interface Args {
  email: string;
  /** Hay usuario recordado con entrada rápida activada y el navegador soporta WebAuthn. */
  activo: boolean;
}

interface Respuesta {
  clave: string;
  opciones: OpcionesDeIngreso | null;
  fallo: MotivoFallo | null;
}

/**
 * Ingreso con la biometría sin contraseña.
 *
 * Al montarse (y cada vez que cambia el email o hace falta un desafío nuevo)
 * pide el desafío por adelantado. Así el clic llega a la ceremonia del
 * navegador sin esperas de red en el medio: en la PWA de Chrome y en Safari el
 * gesto del clic sigue vivo cuando sale el diálogo de huella / Face ID.
 */
export function useQuickLogin({ email, activo }: Args) {
  const [recarga, setRecarga] = useState(0);
  const [respuesta, setRespuesta] = useState<Respuesta | null>(null);
  const [ingresando, setIngresando] = useState(false);
  const [falloAlIngresar, setFalloAlIngresar] = useState<MotivoFallo | null>(null);

  const clave = `${email}#${recarga}`;

  useEffect(() => {
    if (!activo || !email) return;
    let vivo = true;
    pedirOpciones(email)
      .then((opciones) => {
        if (vivo) setRespuesta({ clave, opciones, fallo: null });
      })
      .catch((err: unknown) => {
        const fallo = err instanceof FalloIngreso ? err.motivo : 'otro';
        if (vivo) setRespuesta({ clave, opciones: null, fallo });
      });
    return () => {
      vivo = false;
    };
  }, [activo, email, clave]);

  // La respuesta es de ESTA clave o todavía no llegó.
  const actual = respuesta?.clave === clave ? respuesta : null;
  const preparando = activo && !!email && !actual;

  /** Devuelve null si entró; si no, el motivo por el que no pudo. */
  const iniciar = useCallback(async (): Promise<MotivoFallo | null> => {
    if (!activo || !email) return 'otro';
    setFalloAlIngresar(null);
    setIngresando(true);
    try {
      let opciones = actual?.opciones ?? null;
      // Con el desafío vencido o sin pedir hay que ir a buscarlo: es el único
      // caso en que hay una espera de red entre el clic y la biometría, y por
      // eso se avisa al servicio de que el gesto pudo perderse.
      let gestoPerdido = false;
      if (!opcionesVigentes(opciones)) {
        opciones = await pedirOpciones(email);
        gestoPerdido = true;
      }
      await ingresarConPasskey(email, opciones, { gestoPerdido });
      return null;
    } catch (err) {
      const motivo = err instanceof FalloIngreso ? err.motivo : 'otro';
      reportarFallo(motivo);
      setFalloAlIngresar(motivo);
      setIngresando(false);
      // El desafío usado ya no sirve: se pide otro (y con él viaja el motivo).
      setRecarga((n) => n + 1);
      return motivo;
    }
  }, [activo, email, actual]);

  const limpiarFallo = useCallback(() => setFalloAlIngresar(null), []);

  return {
    preparando,
    ingresando,
    listo: !!actual?.opciones,
    /** El fallo del último intento, o el de pedir el desafío (por ejemplo, el límite). */
    fallo: falloAlIngresar ?? actual?.fallo ?? null,
    iniciar,
    limpiarFallo,
  };
}
